import React from "react";
import sortBy from "lodash/sortBy";
import findKey from "lodash/findKey";
import capitalize from "lodash/capitalize";
import moment from "moment";
import i18n from "../../../../i18n";

import Tooltip from "../../../../parts/tooltip";
import { findPercentComplete } from "../../../../utils/cycle-progress";
import SvgIcon from "../../../../parts/SvgIcon";
import { getCurrentSessionWorkoutName } from "../../../../utils/cycle-helpers";

class SingleDayCycleBar extends React.Component {
  constructor(props) {
    super(props);

    // Find the current program and cycle details
    const { cycle, cycleExercises, activeDay, exerciseCycleDetails } = props;

    const cycleProgram = cycle.programs.find((program) => {
      return program.order === exerciseCycleDetails.program;
    });

    // Sort sessions by date
    cycleProgram.sessions = sortBy(cycleProgram.sessions, ["week", "order"]);

    // Get the program's instance number, to avoid getting exercises from other instances
    let programInstance = 1;
    (cycleProgram?.instances || []).forEach((instance) => {
      if (
        moment(activeDay).isBetween(instance.startDate, instance.endDate) ||
        moment(activeDay).isSame(instance.startDate, "day") ||
        moment(activeDay).isSame(instance.endDate, "day")
      )
        programInstance = instance.instance;
    });

    // Organize the program's exercises into sessions by day
    let sessions = cycleProgram.sessions.map((session, it) => {
      const sessionExercises = cycleExercises.filter((ex) => {
        if (
          ex.cycle.program == cycleProgram.order &&
          ex.cycle.programWeek == session.week &&
          ex.cycle.programSession == session.order &&
          ex.cycle.programInstance == programInstance
        )
          return ex;
      });
      const date = sessionExercises.length
        ? moment.utc(sessionExercises[0].date).format("MMMM D, YYYY")
        : i18n.t("calendar.singleDayCycleBar.removed", { ns: "dashboard" });

      return {
        session,
        sessionExercises,
        date,
      };
    });

    sessions = sessions.map((session, it) => {
      session.order = it + 1;
      return session;
    });

    this.state = {
      nextSession: moment(),
      prevSession: moment(),
      sessions,
    };
  }

  render() {
    const { cycle, activeDay, nav, exerciseCycleDetails, isAthlete, pathname } =
      this.props;

    const path = isAthlete ? "/actions/refresh" : pathname || "/";

    const cycleProgram = cycle.programs.find((program) => {
      return program.order === exerciseCycleDetails.program;
    });
    const isUser = cycle.type === "user";
    const navOffset = parseInt(nav);
    cycleProgram.sessions = sortBy(cycleProgram.sessions, ["week", "order"]);
    const activeDayFormat = activeDay.format("MMMM D, YYYY");
    const removedLabel = i18n.t("calendar.singleDayCycleBar.removed", { ns: "dashboard" });

    // Find the sessions that will display in the cycle bar
    // User created programs don't include deleted sessions
    const sessions = this.state.sessions;
    const programSession = isUser
      ? Number(findKey(sessions, { date: activeDayFormat }))
      : Number(
          findKey(cycleProgram.sessions, {
            week: exerciseCycleDetails.programWeek,
            order: exerciseCycleDetails.programSession,
          })
        );
    let firstBarSessionIndex = Math.floor(programSession / 10 + navOffset) * 10;
    let lastBarSessionIndex =
      Math.floor(programSession / 10 + navOffset) * 10 + 10;

    // Handle the indexes being out of range
    if (firstBarSessionIndex < 0) {
      firstBarSessionIndex = 0;
      lastBarSessionIndex = 10;
    }
    if (lastBarSessionIndex > sessions.length - 1) {
      firstBarSessionIndex = Math.floor((sessions.length - 1) / 10) * 10;
      lastBarSessionIndex = sessions.length + 1;
    }

    const sessionsToShow = sessions.slice(
      firstBarSessionIndex,
      lastBarSessionIndex
    );

    const progress = findPercentComplete(cycleProgram, exerciseCycleDetails);

    const workoutName =
      sessions &&
      Array.isArray(sessions) &&
      sessions.length > 0 &&
      exerciseCycleDetails &&
      exerciseCycleDetails.programSession &&
      exerciseCycleDetails.programWeek
        ? getCurrentSessionWorkoutName(
            sessions,
            exerciseCycleDetails.programWeek,
            exerciseCycleDetails.programSession,
            true
          )
        : undefined;

    return (
      <div className="program-bar">
        <p className="s1 clipped w-fit">{capitalize(cycleProgram.name)}</p>
        <p className="p1 mt-1">{capitalize(cycle.name)}</p>
        {workoutName && (
          <p className="p1 mt-1 opacity-70">{capitalize(workoutName)}</p>
        )}

        <div className="flex justify-between mt-4">
          <p className="p2">{i18n.t("calendar.singleDayCycleBar.progress", { ns: "dashboard" })}</p>
          <p className="p1">{i18n.t("calendar.singleDayCycleBar.percentComplete", { ns: "dashboard", value: progress >= 0 ? progress : 0 })}</p>
        </div>

        <div className="percent-bar-container">
          <div className="percent-bar" style={{ width: `${progress}%` }}></div>
        </div>

        <div className="workouts-frame">
          <a
            href={`/actions/refresh?nav=${parseInt(nav) - 1}`}
            aria-disabled={firstBarSessionIndex < 10}
            onClick={(e) => {
              if (firstBarSessionIndex < 10) e.preventDefault();
            }}
          >
            <SvgIcon
              src="/img/new-icons/arrow-square-left-gradient.svg"
              noFilterOnLight={true}
            />
          </a>

          <ol className="workouts">
            {sessionsToShow.map((session, it) => {
              if (
                moment(session.date, "MMM D, yyyy").isBefore(moment()) &&
                !(session.date === removedLabel) &&
                !(programSession + 1 == session.order)
              ) {
                return (
                  <li className="complete" key={`session-${it}`}>
                    <Tooltip body={session.date} id={`tooltip-session-${it}`}>
                      <a
                        href={`${path}?view=day&date=${moment(
                          new Date(session.date)
                        ).format("YYYY-MM-DD")}`}
                      >
                        <b>{session.order}</b>
                      </a>
                    </Tooltip>
                  </li>
                );
              } else if (programSession + 1 == session.order) {
                const momentDate = moment(new Date(session.date));
                return (
                  <li className="current" key={`session-${it}`}>
                    <Tooltip
                      body={momentDate.format("MMMM D, YYYY")}
                      id={`tooltip-session-${it}`}
                    >
                      <a
                        href={`${path}?view=day&date=${momentDate.format(
                          "YYYY-MM-DD"
                        )}`}
                      >
                        <b>{session.order}</b>
                      </a>
                    </Tooltip>
                  </li>
                );
              } else if (session.date === removedLabel) {
                return (
                  <li key={`session-${it}`}>
                    <Tooltip body={session.date} id={`tooltip-session-${it}`}>
                      <span className="removed-session">
                        <b>{session.order}</b>
                      </span>
                    </Tooltip>
                  </li>
                );
              } else {
                const momentDate = moment(new Date(session.date));

                return (
                  <li key={`session-${it}`}>
                    <Tooltip
                      body={momentDate.format("MMMM D, YYYY")}
                      id={`tooltip-session-${it}`}
                    >
                      <a
                        href={`${path}?view=day&date=${momentDate.format(
                          "YYYY-MM-DD"
                        )}`}
                      >
                        <b>{session.order}</b>
                      </a>
                    </Tooltip>
                  </li>
                );
              }
            })}
          </ol>

          <a
            href={`/actions/refresh?nav=${parseInt(nav) + 1}`}
            aria-disabled={firstBarSessionIndex > sessions.length - 11}
            onClick={(e) => {
              if (firstBarSessionIndex > sessions.length - 11)
                e.preventDefault();
            }}
          >
            <SvgIcon
              src="/img/new-icons/arrow-square-right-gradient.svg"
              noFilterOnLight={true}
            />
          </a>
        </div>
      </div>
    );
  }
}

export default SingleDayCycleBar;
